import * as React from 'react'

import { useSession } from 'next-auth/react'

import { UserIdEnum } from '@/enums/UserIdEnum'
import { getMarket } from '@/helpers/getMarket'
import { getMarketingConsentId } from '@/helpers/getMarketingConsentId'
import { getUserId } from '@/helpers/getUserId'
import { hashString } from '@/helpers/hashString'
import { useUser } from '@/hooks/sap/user/useUser'
import { useSiteContext } from '@/hooks/useSiteContext'
import { useTenant } from '@/hooks/useTenant'
import { eventBus } from '@/lib/eventBus/eventBus'
import { TrackingTopic } from '@/lib/eventBus/trackingTopics'
import { LanguageContext } from '@/providers/TranslationContextProvider'

import type {
  CartLineItem,
  ListContext,
  TrackingContext,
  TrackingEventByTopic,
  TrackingPayloadByTopic,
} from '@/lib/eventBus/trackingEvents'

/** Enriches every tracking event with user/session/site/page context once, here, then puts it on the
 * bus — subscribers (GA4, Relewise, ...) receive the same enriched payload and never read context themselves. */
export const useTrackEvent = () => {
  const { siteContext } = useSiteContext()
  const { language } = React.useContext(LanguageContext)
  const { status, data: session } = useSession()
  const { data: userData } = useUser()
  const tenant = useTenant()

  const hashCacheRef = React.useRef<{ source: string; promise: Promise<string> } | null>(null)

  const getHashedUid = React.useCallback(() => {
    const identitySource = userData?.uid || session?.id

    if (!identitySource) {
      return Promise.resolve<string>(UserIdEnum.Anonymous)
    }

    if (hashCacheRef.current?.source !== identitySource) {
      hashCacheRef.current = { source: identitySource, promise: hashString(identitySource) }
    }

    return hashCacheRef.current.promise
  }, [userData?.uid, session?.id])

  const buildContext = React.useCallback((): TrackingContext => {
    const userId = getUserId(session)

    return {
      hashedUid: getHashedUid(),
      isAnonymous: status !== 'authenticated',
      authenticatedUserId: userId === UserIdEnum.Anonymous ? undefined : userId,
      marketingConsentId: getMarketingConsentId(),
      marketDomain: getMarket(tenant.id, siteContext),
      customerId: userData?.orgUnit?.uid,
      userSegment: userData?.customerType,
      roles: userData?.roles,
      userLanguage: userData?.language?.isocode,
      siteLanguage: language,
      siteContext,
      market: userData?.market,
      customerSegment1: userData?.customerStatGroup1,
      customerSegment2: userData?.customerStatGroup2,
      currency: tenant.currency,
      page: {
        title: document.title,
        location: window.location.href,
        path: window.location.pathname,
      },
    }
  }, [getHashedUid, session, status, userData, siteContext, language, tenant])

  /** Emits any topic with its typed payload. A named wrapper below exists only where a call needs a guard
   * or a shorter signature — a new event needs no new wrapper. */
  const track = React.useCallback(
    <Topic extends keyof TrackingPayloadByTopic>(topic: Topic, data: TrackingPayloadByTopic[Topic]) => {
      if (typeof document === 'undefined') {
        return
      }

      // TypeScript cannot relate a generic topic across the two mapped types; the signature above ties them.
      eventBus.emit(topic, { context: buildContext(), data } as TrackingEventByTopic[Topic])
    },
    [buildContext]
  )

  const trackAddToCart = React.useCallback(
    (items: CartLineItem[], listContext?: ListContext) => {
      track(TrackingTopic.CartItemAdded, { items, listContext })
    },
    [track]
  )

  const trackLogin = React.useCallback(() => {
    if (status !== 'authenticated') {
      return
    }

    track(TrackingTopic.UserLoggedIn, null)
  }, [track, status])

  const trackPageView = React.useCallback(() => {
    track(TrackingTopic.PageViewed, null)
  }, [track])

  const trackSearch = React.useCallback(
    (searchTerm: string) => {
      if (!searchTerm) {
        return
      }

      track(TrackingTopic.SearchPerformed, { searchTerm })
    },
    [track]
  )

  const trackError = React.useCallback(
    (error: Error, context?: string) => {
      track(TrackingTopic.TrackingErrorOccurred, { error, context })
    },
    [track]
  )

  return { track, trackAddToCart, trackLogin, trackPageView, trackSearch, trackError }
}
